// src/components/charts/assistant/BarChart.tsx
/**
 * Assistant Bar Chart
 * 
 * A bar chart optimized for the assistant's dark card UI with:
 * - Custom tooltip styling
 * - Semantic coloring based on positive/negative values
 * - Compact grid layout
 */

import React, { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { 
  ASSISTANT_CHART_THEME, 
  ASSISTANT_CHART_HEIGHT,
  getFormatter,
  type FormatterOptions,
} from "./theme";

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

export interface AssistantBarChartProps {
  /** Category labels */
  categories: string[];
  /** Data values */
  values: number[];
  /** Value formatting options */
  formatOptions?: FormatterOptions;
  /** Y-axis label */
  yAxisName?: string;
  /** Color bars based on positive/negative value */
  colorByValue?: boolean;
  /** Bar color */
  barColor: string;
  /** Bar hover color */
  barHoverColor: string;
  /** Chart height (defaults to ASSISTANT_CHART_HEIGHT) */
  height?: number;
}

// ─────────────────────────────────────────────────────────────────────────────
// Component
// ─────────────────────────────────────────────────────────────────────────────

const AssistantBarChartInner: React.FC<AssistantBarChartProps> = ({ 
  categories, 
  values, 
  formatOptions, 
  yAxisName, 
  colorByValue = false, 
  barColor, 
  barHoverColor,
  height = ASSISTANT_CHART_HEIGHT,
}) => {
  const formatter = getFormatter(formatOptions);
  const semantic = ASSISTANT_CHART_THEME.semantic;
  
  const option = useMemo(() => {
    // For colorByValue, create data with individual colors
    const seriesData = colorByValue 
      ? values.map(v => ({
          value: v,
          itemStyle: {
            borderRadius: v >= 0 ? [4, 4, 0, 0] : [0, 0, 4, 4],
            color: v >= 0 ? semantic.positive : semantic.negative,
          },
        }))
      : values;
    
    return {
      backgroundColor: ASSISTANT_CHART_THEME.background,
      tooltip: {
        trigger: "axis" as const,
        axisPointer: { type: "shadow" as const },
        backgroundColor: ASSISTANT_CHART_THEME.tooltip.background,
        borderColor: ASSISTANT_CHART_THEME.tooltip.border,
        borderWidth: 1,
        padding: [10, 14],
        textStyle: {
          color: ASSISTANT_CHART_THEME.text.primary,
          fontSize: 12,
        },
        formatter: (params: unknown) => {
          const items = params as Array<{ name: string; value: number }>;
          if (!items?.[0]) return "";
          const item = items[0];
          const val = item.value;
          const color = colorByValue 
            ? (val >= 0 ? semantic.positive : semantic.negative)
            : barColor;
          return `
            <div style="font-weight:500;margin-bottom:4px;color:rgba(255,255,255,0.85)">${item.name}</div>
            <div style="display:flex;align-items:center;gap:6px">
              <span style="display:inline-block;width:8px;height:8px;border-radius:2px;background:${color}"></span>
              <span style="color:${color};font-weight:500">${formatter(val)}</span>
            </div>
          `;
        },
      },
      grid: {
        left: 8,
        right: 16,
        top: yAxisName ? 32 : 12,
        bottom: 28,
        containLabel: true,
      },
      xAxis: {
        type: "category" as const,
        data: categories,
        axisLabel: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 11,
          fontWeight: 500,
        },
        axisTick: { show: false },
        axisLine: { show: false },
      },
      yAxis: {
        type: "value" as const,
        ...(yAxisName ? { name: yAxisName } : {}),
        nameTextStyle: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 10,
          padding: [0, 0, 4, 0],
        },
        axisLabel: {
          color: ASSISTANT_CHART_THEME.text.muted,
          fontSize: 10,
          formatter,
        },
        splitLine: {
          lineStyle: {
            color: ASSISTANT_CHART_THEME.grid.line,
            type: "dashed" as const,
          },
        },
        axisLine: { show: false },
        axisTick: { show: false },
      },
      series: [{
        type: "bar" as const,
        data: seriesData,
        barWidth: "55%",
        barMaxWidth: 52,
        ...(!colorByValue ? {
          itemStyle: {
            borderRadius: [4, 4, 0, 0],
            color: barColor,
          },
          emphasis: {
            itemStyle: {
              color: barHoverColor,
            },
          },
        } : {
          emphasis: {
            itemStyle: {
              opacity: 0.85,
            },
          },
        }),
      }],
      animationDuration: 500,
      animationEasing: "cubicOut" as const,
    };
  }, [categories, values, yAxisName, colorByValue, barColor, barHoverColor, formatter, semantic]);

  return (
    <ReactECharts
      option={option}
      style={{ height, width: "100%" }}
      opts={{ renderer: "canvas" }}
      notMerge
    />
  );
};

// Custom comparison for memo
function arePropsEqual(
  prev: AssistantBarChartProps,
  next: AssistantBarChartProps
): boolean {
  // Quick checks for primitives
  if (
    prev.yAxisName !== next.yAxisName ||
    prev.colorByValue !== next.colorByValue ||
    prev.barColor !== next.barColor ||
    prev.barHoverColor !== next.barHoverColor ||
    prev.height !== next.height
  ) {
    return false;
  }

  // Check arrays
  if (prev.categories.length !== next.categories.length) return false;
  if (prev.values.length !== next.values.length) return false;
  
  if (prev.categories !== next.categories && prev.categories.some((c, i) => c !== next.categories[i])) {
    return false;
  }
  if (prev.values !== next.values && prev.values.some((v, i) => v !== next.values[i])) {
    return false;
  }

  // Check formatOptions
  if (prev.formatOptions !== next.formatOptions) {
    if (!prev.formatOptions || !next.formatOptions) return false;
    if (
      prev.formatOptions.unit !== next.formatOptions.unit ||
      prev.formatOptions.isChangeMetric !== next.formatOptions.isChangeMetric
    ) {
      return false;
    }
  }

  return true;
}

const AssistantBarChart = React.memo(AssistantBarChartInner, arePropsEqual);

export default AssistantBarChart;

